import { z } from "zod";
import { defineTool } from "../harness/index.js";
import type { AgentConnection } from "../connection/agent-connection.js";
import { textResult } from "./helpers.js";

export function buildCodeReviewTools(connection: AgentConnection) {
  return [
    defineTool(
      "approve_code_review",
      "Approve the code review and exit. Use when the diff passes all review criteria. Takes only a summary — for changes, use request_code_changes with a structured issues[] list.",
      {
        summary: z.string().describe("Brief summary of what was reviewed and why it looks good"),
      },
      async ({ summary }) => {
        const content = `**Code Review: Approved** :white_check_mark:\n\n${summary}`;
        await connection.call("submitCodeReviewResult", {
          sessionId: connection.sessionId,
          approved: true,
          content,
        });
        connection.sendEvent({
          type: "code_review_complete",
          result: "approved",
          summary,
        });
        return textResult("Code review approved. Exiting.");
      },
    ),
    defineTool(
      "request_code_changes",
      "Request changes during code review and exit. Use when substantive issues must be fixed before merge. Each issue: { file, line?, severity: critical|major|minor, description }.",
      {
        issues: z
          .array(
            z.object({
              file: z.string().describe("File path where the issue was found"),
              line: z.number().optional().describe("Line number (if applicable)"),
              severity: z.enum(["critical", "major", "minor"]).describe("Issue severity"),
              description: z.string().describe("What is wrong and how to fix it"),
            }),
          )
          .describe("List of issues found during review"),
        summary: z.string().describe("Brief overall summary of the review findings"),
      },
      async ({ issues, summary }) => {
        const issueLines = issues
          .map((issue) => {
            const loc = issue.line ? `:${issue.line}` : "";
            return `- **[${issue.severity}]** \`${issue.file}${loc}\`: ${issue.description}`;
          })
          .join("\n");

        const content = `**Code Review: Changes Requested** :warning:\n\n${summary}\n\n${issueLines}`;
        await connection.call("submitCodeReviewResult", {
          sessionId: connection.sessionId,
          approved: false,
          content,
        });
        connection.sendEvent({
          type: "code_review_complete",
          result: "changes_requested",
          summary,
          issues,
        });
        return textResult("Code review complete — changes requested. Exiting.");
      },
    ),
  ];
}
